// src/pages/SelectSubscription.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Loader, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from "react-toastify";
import api from '../services/api';
import { getPublicPlans } from '../services/subscriptionService';
import { SubscriptionPlan } from '../types';

interface RegistrationState {
  userType?: 'doctor' | 'public';
  // Shared fields
  email?: string;
  // Short-lived token issued after email verification
  registrationToken?: string;
  // Doctor professional fields (collected after verification)
  specialization?: string;
  licensingBody?: string;
  practiceCountry?: string;
  qualification?: string;
  clinicHospitalName?: string;
}

const SelectSubscription: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as Partial<RegistrationState>;
  const isDoctor = state?.userType !== 'public';

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Ensure the user reached this page with a verified registration token
  useEffect(() => {
    if (!state?.registrationToken || !state?.email) {
      navigate('/register', { replace: true });
      return;
    }

    const loadPlans = async () => {
      setLoading(true);
      try {
        let list = await getPublicPlans(isDoctor ? 'doctor' : 'public');
        // If no user-type-specific plans found for public, try fallback to all available plans
        if ((!list || list.length === 0) && !isDoctor) {
          list = await getPublicPlans('doctor');
        }
        setPlans(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load subscription plans:", err);
        setError("Failed to load subscription plans. Please try again.");
        toast.error("Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleteRegistration = async (plan: SubscriptionPlan) => {
    setError('');
    setSubmittingPlanId(plan.id);

    try {
      if (isDoctor) {
        const payload = {
          registrationToken: state.registrationToken,
          subscription_plan_id: plan.id,
          specialization: state.specialization,
          licensingBody: state.licensingBody,
          practiceCountry: state.practiceCountry,
          qualification: state.qualification,
          clinicHospitalName: state.clinicHospitalName,
        };

        const response = await api.post("/doctor/register", payload);

        if (response.data?.success) {
          toast.success(`Registration successful with the ${plan.planName} plan! Please login to continue.`);
          navigate("/login", { replace: true });
        } else {
          const errorMessage = response.data?.message || "Registration failed. Please try again.";
          setError(errorMessage);
          toast.error(errorMessage);
        }
      } else {
        const payload = {
          registrationToken: state.registrationToken,
          subscription_plan_id: plan.id,
        };

        const response = await api.post("/public/register", payload);

        if (response.data?.success) {
          toast.success(`Registration successful with the ${plan.planName} plan! Please login to continue.`);
          navigate("/login", { replace: true });
        } else {
          const errorMessage = response.data?.message || "Registration failed. Please try again.";
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (err: any) {
      console.error("Registration error:", err);
      let errorMessage = "An error occurred during registration";

      if (err.response) {
        const data = err.response.data;
        const status = err.response.status;
        if (data?.message) {
          errorMessage = data.message;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (status === 400) {
          errorMessage = data?.message || "Invalid registration data. Please check all fields.";
        } else if (status === 409) {
          errorMessage = isDoctor
            ? "Email or medical licence number already exists."
            : "An account with this email already exists.";
        } else if (status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = `Registration failed (${status}). Please try again.`;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmittingPlanId(null);
    }
  };

  const formatPrice = (plan: SubscriptionPlan) => {
    const price = Number(plan.price);
    return plan.currency === "USD"
      ? `$${price}`
      : `${price} ${plan.currency}`;
  };

  return (
    <div className="min-h-screen w-full bg-[#F8FAFA] flex flex-col overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <img
            src="/logo/DiagnomIQ.png"
            alt="DiagnomIQ"
            className="h-12 w-auto object-contain"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="relative mb-10">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="absolute left-0 top-2 sm:top-1/2 sm:-translate-y-1/2 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors duration-200 cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="text-center px-14 sm:px-0">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
              {isDoctor ? "Choose Your Professional Subscription Plan" : "Choose Your Subscription Plan"}
            </h1>
            <p className="text-gray-500 text-sm mt-2">
              {isDoctor
                ? "Select a plan that fits your clinical practice. A plan is required to complete your registration."
                : "Select a plan to access informational MDT reports. A plan is required to complete your registration."}
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-start justify-center mb-6">
            <div className="flex items-start space-x-2 text-red-600 text-sm bg-red-50 p-3 rounded-md max-w-lg w-full">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="h-8 w-8 animate-spin text-[#36B8B8]" />
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500">
              No subscription plans are available at the moment. Please try again later.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {plans.map((plan) => {
              const isSubmitting = submittingPlanId === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`
                    relative flex flex-col h-full w-full rounded-lg p-6 bg-[#EBEBEB] border border-neutral-200
                    shadow-xl shadow-neutral-500/15 transition-all duration-300 transform-gpu gap-4
                    hover:shadow-2xl hover:shadow-neutral-500/25 hover:-translate-y-1
                  `}
                >
                  {/* Plan tag */}
                  {plan.planTag ? (
                    <div className="flex items-center justify-end flex-shrink-0">
                      <span className="inline-flex max-w-[60%] px-3 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider truncate overflow-hidden whitespace-nowrap bg-[#36B8B8]/10 text-[#1d7a7a]">
                        {plan.planTag}
                      </span>
                    </div>
                  ) : null}

                  {/* Plan Name */}
                  <h2 className="text-xl font-semibold text-center uppercase tracking-wide truncate overflow-hidden whitespace-nowrap text-gray-900">
                    {plan.planName}
                  </h2>

                  {/* Price */}
                  <div className="flex items-baseline justify-center gap-2 flex-shrink-0">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(plan)}
                    </p>
                    <p className="text-xs text-gray-500">
                      /{plan.durationValue} {plan.durationType}
                    </p>
                  </div>

                  {/* Description */}
                  {plan.description ? (
                    <p className="text-sm text-gray-600 text-center leading-relaxed">
                      {plan.description}
                    </p>
                  ) : null}

                  {/* Divider */}
                  <div className="w-full border-t border-neutral-300" />

                  {/* Features */}
                  {plan.includedFeatures && plan.includedFeatures.length > 0 ? (
                    <ul className="flex-1 min-h-0 overflow-y-auto max-h-[180px] pr-1 space-y-2 text-sm">
                      {plan.includedFeatures.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="shrink-0 mt-0.5 text-[#36B8B8]">
                            <Check size={16} strokeWidth={2.5} />
                          </span>
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex-1 min-h-0" />
                  )}

                  {/* Select & complete registration */}
                  <div className="flex-shrink-0 mt-auto">
                    <button
                      type="button"
                      onClick={() => handleCompleteRegistration(plan)}
                      disabled={submittingPlanId !== null}
                      className="
                        w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-[#36B8B8]
                        hover:bg-[#2fa3a3] transition-colors duration-200 shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#36B8B8]
                        disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer
                      "
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin inline" />
                          Registering...
                        </>
                      ) : (
                        'Select Plan'
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && plans.length > 0 && (
          <div className="mt-8 text-center space-y-1">
            <p className="text-xs text-gray-500">
              By completing registration you agree to the selected subscription plan and confirm your acceptance of DiagnomIQ's{" "}
              <a
                href="/legal?tab=terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary-600 hover:text-primary-700"
              >
                Terms &amp; Conditions
              </a>
              {", "}
              <a
                href="/legal?tab=privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary-600 hover:text-primary-700"
              >
                Privacy Policy
              </a>
              {", and "}
              <a
                href="/legal?tab=medical-disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary-600 hover:text-primary-700"
              >
                Medical Disclaimer
              </a>
              {"."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSubscription;